import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  MgtGroupDailyOrderCustomerVisitDto,
  SearchDateTimeRange,
} from "@/api/swagger";
import { AxiosResponse } from "axios";
import AnalysisWrapper from "@/components/Feature/AnalysisWrapper";
import { convertDateToMoment } from "@/helper/dateHelper";

export type CustomerVisitCountAnalysisSearchOrderDailyCountApi = (config: {
  time: SearchDateTimeRange;
}) => Promise<AxiosResponse<Array<MgtGroupDailyOrderCustomerVisitDto>>>;

interface Props {
  searchOrderDailyCountApi: CustomerVisitCountAnalysisSearchOrderDailyCountApi;
}

const tickFormatter = (value: string) => {
  const weekDaysName = ["週日", "週一", "週二", "週三", "週四", "週五", "週六"];
  const moment = convertDateToMoment(new Date(value));

  return `${moment.format("MM/DD")} ${weekDaysName[moment.day()]}`;
};

const Tick: React.FC<{ x: number; y: number; payload: { value: string } }> = ({
  x,
  y,
  payload,
}) => {
  const weekDaysName = ["週日", "週一", "週二", "週三", "週四", "週五", "週六"];
  const moment = convertDateToMoment(new Date(payload.value));

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor={"middle"} fill="#666">
        <tspan x="0" dy="1.2em">
          {moment.format("MM/DD")}
        </tspan>
        <tspan x="0" dy="1.2em">
          {weekDaysName[moment.day()]}
        </tspan>
      </text>
    </g>
  );
};

const tooltipLabel = {
  firstVisitCnt: "新客",
  secondVisitCnt: "2次客",
  thirdVisitCnt: "3次 + 客",
};

const tooltipFormatter = (value: number, name: keyof typeof tooltipLabel) => {
  return [value, tooltipLabel?.[name] ?? ""];
};

const CustomerVisitCountAnalysis: React.FC<Props> = ({
  searchOrderDailyCountApi,
}) => {
  return (
    <AnalysisWrapper
      title={"來客分析"}
      unit={"人"}
      legends={[
        { color: "#B5DFC4", label: "新客" },
        { color: "#B8B5E6", label: "2次客" },
        { color: "#FEDE9F", label: "3次 + 客" },
      ]}
      searchApi={searchOrderDailyCountApi}
      chart={({ data, xTickInterval }) => {
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              width={500}
              height={400}
              data={data}
              margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 20,
              }}
            >
              <CartesianGrid strokeDasharray={"3 3"} />
              <XAxis
                dataKey={"date"}
                interval={xTickInterval}
                tickFormatter={tickFormatter}
                tick={(props: any) => <Tick {...props} />}
              />
              <YAxis />
              <Tooltip formatter={tooltipFormatter} />
              <Bar dataKey={"firstVisitCnt"} stackId={"1"} fill={"#B5DFC4"} />
              <Bar dataKey={"secondVisitCnt"} stackId={"1"} fill={"#B8B5E6"} />
              <Bar dataKey={"thirdVisitCnt"} stackId={"1"} fill={"#FEDE9F"} />
            </BarChart>
          </ResponsiveContainer>
        );
      }}
    />
  );
};

export default CustomerVisitCountAnalysis;
